import type { Faq as FaqType } from 'libs/http/api/content/content.types';

type Translate = (key: string) => string;

export const getFaqData = (t: Translate): FaqType[] => [
  {
    id: 'overwrite-existing',
    question: t('Will it overwrite what my team has already entered?'),
    answer: t(
      'Not unless you switch that on for a specific field. The default is to fill empty fields and leave populated ones alone, with the register value held alongside so you can compare the two.',
    ),
    default_opened: true,
  },
  {
    id: 'unmatched-records',
    question: t('What happens to records it cannot match?'),
    answer: t(
      'They go to a review queue with the candidate entities and why each scored the way it did. A person decides. We do not write a weak match into your CRM to improve a coverage figure.',
    ),
  },
  {
    id: 'coverage-outside-us-uk',
    question: t('How good is coverage outside the US and UK?'),
    answer: t(
      '400+ registers across 200+ countries, collected from each national register directly. Ask for a match-rate test on a sample of your own database before committing to anything.',
    ),
  },
  {
    id: 'merge-duplicates',
    question: t('Do you merge the duplicates, or only flag them?'),
    answer: t(
      'Grouping is automatic and based on the registration number rather than name similarity. The merge is yours to approve, with a log of what went into what.',
    ),
  },
  {
    id: 'data-outside-crm',
    question: t('Can we get the same data outside the CRM?'),
    answer: t(
      'Yes. The same records are available through the API, as a bulk file, or in the platform, so your warehouse and your CRM are not running on two different versions of a company.',
    ),
  },
  {
    id: 'recheck-frequency',
    question: t('How often is a matched record re-checked?'),
    answer: t(
      'Daily. A matched record stays bound to its register entry, so when a filing changes the field changes with it and the write carries a timestamp. You decide which field groups are allowed to trigger a write, so a new registered address can update quietly while a change of ownership is surfaced rather than applied in the background.',
    ),
  },
  {
    id: 'dissolved-or-acquired',
    question: t('What happens when a company is dissolved or acquired?'),
    answer: t(
      'The status field moves and the change lands on the record timeline instead of being applied silently. Where the register records a successor or a new parent, that link comes through as well, so an acquired subsidiary still resolves upward to the group that now owns it.',
    ),
  },
  {
    id: 'enrich-people',
    question: t('Does it enrich people as well as companies?'),
    answer: t(
      'It resolves companies. Directors and officers arrive attached to the company record because the register files them there, so you can see who is behind an account. Contact data is a separate product and is not what this is doing.',
    ),
  },
  {
    id: 'crm-access',
    question: t('What access do you need to our CRM?'),
    answer: t(
      'An admin installs it and scopes it to the objects and fields you nominate. It reads the company records you point it at and writes only into the fields you have mapped. Nothing outside that mapping is touched, and the mapping stays under your control after go-live.',
    ),
  },
  {
    id: 'first-backfill-time',
    question: t('How long does the first backfill take?'),
    answer: t(
      'It depends on the size of the database and how much of it already carries a registration number. A database keyed on numbers resolves quickly. One built on free-text company names takes longer, because more of it lands in the review queue by design. We will tell you which one you have after the sample test.',
    ),
  },
];
